import React, { useRef, useState } from 'react';
import MovieCard from './MovieCard';
import { FiChevronLeft, FiChevronRight, FiRefreshCw } from 'react-icons/fi';
import { FormattedMovie } from '../types/movie';
import InfoIcon from './InfoIcon';

interface CarouselProps {
  title: string;
  tooltipText: string;
  movies: FormattedMovie[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
  id: string;
}

const Carousel: React.FC<CarouselProps> = ({ title, tooltipText, movies, loading, error, refresh, id }) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const scroll = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const { clientWidth } = carouselRef.current;
      const scrollAmount = direction === 'left' ? -clientWidth : clientWidth;
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="container-custom py-8 animate-fade-in" id={id}>
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-secondary-dark mb-0">{title}</h2>
          <InfoIcon tooltipText={tooltipText} />
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 border-4 border-primary/20 rounded-full animate-spin" />
            <div className="absolute inset-0 border-t-4 border-primary rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-custom py-8 animate-fade-in" id={id}>
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-secondary-dark mb-0">{title}</h2>
          <InfoIcon tooltipText={tooltipText} />
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-secondary-dark mb-4">
            Oops! We couldn't load the movies at the moment.
          </p>
          <button
            onClick={refresh}
            className="inline-flex items-center space-x-2 btn-primary"
          >
            <FiRefreshCw className="w-5 h-5" />
            <span>Try Again</span>
          </button>
        </div>
      </div>
    );
  }

  if (!movies.length) {
    return (
      <div className="container-custom py-8 animate-fade-in" id={id}>
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-secondary-dark mb-0">{title}</h2>
          <InfoIcon tooltipText={tooltipText} />
        </div>
        <div className="bg-background-light border border-primary/10 rounded-xl p-6 text-center">
          <p className="text-secondary-dark mb-4">
            No movies found for this period.
          </p>
          <button
            onClick={refresh}
            className="inline-flex items-center space-x-2 btn-secondary"
          >
            <FiRefreshCw className="w-5 h-5" />
            <span>Refresh</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      id={id}
      className="container-custom py-8 animate-fade-in"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-3xl md:text-4xl font-heading font-bold text-secondary-dark mb-0">{title}</h2>
        <InfoIcon tooltipText={tooltipText} />
      </div>
      <div className="relative">
        {/* Mobile Grid Layout */}
        <div className="md:hidden grid grid-cols-2 gap-4">
          {movies.map((movie) => (
            <div key={movie._id} className="animate-slide-up">
              <MovieCard
                id={movie._id} // Pass the string _id
                poster={movie.poster}
                title={movie.title}
                releaseDate={movie.releaseDate}
                hypeScore={movie.hypeScore}
                slug={movie.slug}
              />
            </div>
          ))}
        </div>
        
        {/* Desktop Carousel */}
        <div className="hidden md:block">
          <div
            ref={carouselRef}
            className="flex space-x-4 overflow-x-auto scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-transparent pb-4"
            style={{ scrollbarWidth: 'thin' }}
          >
            {movies.map((movie) => (
              <div key={movie._id} className="flex-shrink-0 animate-slide-up">
                <MovieCard
                  id={movie._id} // Pass the string _id
                  poster={movie.poster}
                  title={movie.title}
                  releaseDate={movie.releaseDate}
                  hypeScore={movie.hypeScore}
                  slug={movie.slug}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Arrows */}
        {movies.length > 4 && (
          <>
            <button
              className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -ml-4 items-center justify-center w-12 h-12 rounded-full bg-white shadow-lg text-secondary-dark hover:text-primary transition-all duration-200 ${
                isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
              }`}
              onClick={() => scroll('left')}
            >
              <FiChevronLeft className="w-6 h-6" />
            </button>
            <button
              className={`hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 -mr-4 items-center justify-center w-12 h-12 rounded-full bg-white shadow-lg text-secondary-dark hover:text-primary transition-all duration-200 ${
                isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
              }`}
              onClick={() => scroll('right')}
            >
              <FiChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Carousel;